/**
 * React Query hooks for the portal carry section.
 */

import { useApiMutation } from '@/hooks/react-query/useApiMutation';
import { useApiQuery } from '@/hooks/react-query/useApiQuery';
import { APIError } from '@/utils/api-client';
import { portalQueryKeys } from '../../shared/hooks/queryKeys';
import {
  CarryService,
  type CarryDocument,
  type CarryProfileUpdateRequest,
} from '../services/carryService';

export function useCarryFunds() {
  return useApiQuery(
    portalQueryKeys.carry.funds(),
    () => CarryService.getFunds(),
    {
      staleTime: 5 * 60 * 1000,
    }
  );
}

export function useCarryHoldings() {
  return useApiQuery(portalQueryKeys.carry.holdings(), () =>
    CarryService.getHoldings()
  );
}

export function useCarryHolding(fundId: string) {
  return useApiQuery(portalQueryKeys.carry.holding(fundId), () =>
    CarryService.getHolding(fundId)
  );
}

export function useCarryHoldingPortfolio(fundId: string) {
  return useApiQuery(portalQueryKeys.carry.holdingPortfolio(fundId), () =>
    CarryService.getHoldingPortfolio(fundId)
  );
}

export function useCarryStructure() {
  return useApiQuery(portalQueryKeys.carry.structure(), () =>
    CarryService.getStructure()
  );
}

export function useCarryDocuments() {
  return useApiQuery(portalQueryKeys.carry.documents(), () =>
    CarryService.getDocuments()
  );
}

export function useCarryFundDocuments(fundId: string) {
  return useApiQuery(portalQueryKeys.carry.fundDocuments(fundId), () =>
    CarryService.getFundDocuments(fundId)
  );
}

export function useDownloadCarryDocument() {
  return useApiMutation<
    void,
    APIError,
    { fundId: string; document: CarryDocument }
  >(({ fundId, document }) => CarryService.downloadDocument(fundId, document), {
    mutationKey: ['portal', 'carry', 'documents', 'download'],
    showSuccessToast: false,
    errorMessage: 'Could not download the document.',
  });
}

export function useUpdateCarryProfile() {
  return useApiMutation<{ id: string }, APIError, CarryProfileUpdateRequest>(
    (data) => CarryService.updateProfile(data),
    {
      mutationKey: ['portal', 'carry', 'profile', 'update'],
      successMessage: 'Phone number updated.',
      invalidateKeys: [portalQueryKeys.me()],
    }
  );
}
